import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Badge,
  Spinner,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../../Network";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { handleAuthError } from "../../utils";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import withAuth from "../withAuth";
import LearningPathMetaFields, { emptyLearningPathMeta } from "./LearningPathMetaFields";
import { learningPathErrorMessage, LearningPathErrorAlert } from "./learningPathErrors";

let keySeq = 0;
const newKey = () => `k${Date.now()}_${keySeq++}`;

const move = (list, index, delta) => {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
};

// Server path -> editable state (each unit/step gets a stable React key).
const toEditableUnits = (units = []) =>
  units.map((u) => ({
    key: newKey(),
    steps: (u.steps || []).map((s) => ({ key: newKey(), curriculumId: s.curriculumId, title: s.title || "" })),
  }));

const toStructurePayload = (units) => ({
  // Units are named "Unit 1", "Unit 2"… by position on the server.
  units: units.map((u) => ({
    steps: u.steps.map((s) => ({ curriculumId: Number(s.curriculumId), title: s.title.trim() || null })),
  })),
});

const pickMeta = (p) => ({
  title: p.title || "",
  icon: p.icon || "python",
  sortOrder: p.sortOrder ?? 0,
  level: p.level || "",
  subcategoryId: p.subcategoryId ?? p.subcategory?.id ?? "",
});

// AI Tools > Learning Paths > edit one path: details, units, and the curricula in each unit.
const LearningPathEditor = () => {
  const { id } = useParams();
  const readOnly = isMarketerReadOnly();
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState(null);
  const [meta, setMeta] = useState(emptyLearningPathMeta());
  const [units, setUnits] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [categories, setCategories] = useState([]);
  const [metaDirty, setMetaDirty] = useState(false);
  const [structureDirty, setStructureDirty] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingStructure, setSavingStructure] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [curriculumFilter, setCurriculumFilter] = useState("");
  // Inline errors for actions that change data (shown until dismissed or retried).
  const [metaError, setMetaError] = useState("");
  const [structureError, setStructureError] = useState("");
  const [visibilityError, setVisibilityError] = useState("");

  document.title = `${path?.title ? `${path.title} | ` : ""}Learning Paths | RYD Admin`;

  const applyPath = (p) => {
    setPath(p);
    setMeta(pickMeta(p));
    setUnits(toEditableUnits(p.units));
    setMetaDirty(false);
    setStructureDirty(false);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [pathRes, curriculumRes, catsRes] = await Promise.all([
          axios.get(`${baseUrl}/admin/learning-path/${id}`),
          axios.get(`${baseUrl}/admin/curriculum/all`),
          axios.get(`${baseUrl}/admin/learning-path/category/all`),
        ]);
        applyPath(pathRes.data.data);
        setCurricula(curriculumRes?.data?.data || []);
        setCategories(catsRes?.data?.data || []);
      } catch (error) {
        handleAuthError(error);
        toast.error(learningPathErrorMessage(error, "Couldn't load this learning path."));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    if (!metaDirty && !structureDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [metaDirty, structureDirty]);

  const curriculumById = useMemo(() => new Map(curricula.map((c) => [c.id, c])), [curricula]);
  const curriculumOptions = useMemo(() => {
    const q = curriculumFilter.trim().toLowerCase();
    return [...curricula]
      .filter((c) => !q || `${c.title} ${c.slug} ${c.level || ""} ${c.language || ""}`.toLowerCase().includes(q))
      .sort((a, b) => Number(!!b.isVisible) - Number(!!a.isVisible) || String(a.title).localeCompare(String(b.title)));
  }, [curricula, curriculumFilter]);

  const updateUnits = (updater) => {
    setUnits((prev) => updater(prev));
    setStructureDirty(true);
  };
  const updateUnit = (unitIndex, patch) =>
    updateUnits((prev) => prev.map((u, i) => (i === unitIndex ? { ...u, ...patch } : u)));
  const updateStep = (unitIndex, stepIndex, patch) =>
    updateUnits((prev) => prev.map((u, i) => (i === unitIndex
      ? { ...u, steps: u.steps.map((s, j) => (j === stepIndex ? { ...s, ...patch } : s)) }
      : u)));

  const addUnit = () =>
    updateUnits((prev) => [...prev, { key: newKey(), steps: [] }]);
  const addStep = (unitIndex, curriculumId) => {
    if (!curriculumId) return;
    updateUnits((prev) => prev.map((u, i) => (i === unitIndex
      ? { ...u, steps: [...u.steps, { key: newKey(), curriculumId: Number(curriculumId), title: "" }] }
      : u)));
  };

  const saveMeta = async () => {
    if (!meta.title.trim()) {
      setMetaError("Title is required.");
      return;
    }
    try {
      setMetaError("");
      setSavingMeta(true);
      const payload = {
        ...meta,
        level: meta.level || null,
        subcategoryId: meta.subcategoryId === "" || meta.subcategoryId == null ? null : Number(meta.subcategoryId),
      };
      const response = await axios.put(`${baseUrl}/admin/learning-path/${id}`, payload);
      const p = response.data.data;
      setPath(p);
      setMeta(pickMeta(p));
      setMetaDirty(false);
      toast.success("Details saved");
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, "Couldn't save the details.");
      setMetaError(message);
      toast.error(message);
    } finally {
      setSavingMeta(false);
    }
  };

  const saveStructure = async () => {
    try {
      setStructureError("");
      setSavingStructure(true);
      const response = await axios.put(`${baseUrl}/admin/learning-path/${id}/structure`, toStructurePayload(units));
      const p = response.data.data;
      setPath(p);
      setUnits(toEditableUnits(p.units));
      setStructureDirty(false);
      toast.success("Units and curricula saved");
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, "Couldn't save the units.");
      setStructureError(message);
      toast.error(message);
    } finally {
      setSavingStructure(false);
    }
  };

  const toggleVisibility = async () => {
    if (structureDirty) {
      setVisibilityError("You have unsaved changes to units or curricula. Save your units first, then publish.");
      return;
    }
    try {
      setVisibilityError("");
      setTogglingVisibility(true);
      const response = await axios.put(`${baseUrl}/admin/learning-path/visibility/${id}`, { isVisible: !path.isVisible });
      setPath(response.data.data);
      toast.success(response.data.data.isVisible ? "Learning path is now visible to learners" : "Learning path hidden from learners");
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, `Couldn't ${path.isVisible ? "hide" : "publish"} this learning path.`);
      setVisibilityError(message);
      toast.error(message);
    } finally {
      setTogglingVisibility(false);
    }
  };

  const stepBadge = (curriculumId) => {
    const c = curriculumById.get(curriculumId);
    if (!c) return <Badge color="danger" title="This curriculum was deleted; learners won't see this step">Missing</Badge>;
    if (!c.isVisible) return <Badge color="warning" title="Hidden curricula are left out of the learner's path">Hidden curriculum</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid><div className="text-center py-5">Loading...</div></Container>
      </div>
    );
  }

  if (!path) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="AI Tools" breadcrumbItem="Learning Paths" />
          <Card><CardBody>
            Learning path not found. <Link to="/ai/learning-paths">Back to learning paths</Link>
          </CardBody></Card>
        </Container>
        <ToastContainer />
      </div>
    );
  }

  const stepTotal = units.reduce((n, u) => n + u.steps.length, 0);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="AI Tools" breadcrumbItem="Learning Paths" />

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <Link to="/ai/learning-paths" className="text-muted small"><i className="mdi mdi-arrow-left me-1" />All learning paths</Link>
            <h4 className="mb-0 mt-1">
              {path.title}{" "}
              <Badge color={path.isVisible ? "success" : "secondary"} className="align-middle">{path.isVisible ? "Visible" : "Hidden"}</Badge>
            </h4>
            <small className="text-muted">Slug: <code>{path.slug}</code></small>
          </div>
          {!readOnly && (
            <Button color={path.isVisible ? "warning" : "success"} outline={!!path.isVisible} onClick={toggleVisibility} disabled={togglingVisibility}>
              {togglingVisibility ? <><Spinner size="sm" className="me-2" />Processing…</> : path.isVisible ? "Hide from learners" : "Publish to learners"}
            </Button>
          )}
        </div>

        <LearningPathErrorAlert message={visibilityError} onDismiss={() => setVisibilityError("")} />

        <Row>
          <Col xl={4}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Details</h5>
                <LearningPathErrorAlert message={metaError} onDismiss={() => setMetaError("")} />
                <LearningPathMetaFields
                  value={meta}
                  onChange={(v) => { setMeta(v); setMetaDirty(true); }}
                  disabled={readOnly || savingMeta}
                  categories={categories}
                />
                {!readOnly && (
                  <div className="d-flex justify-content-end mt-3">
                    <Button color="primary" onClick={saveMeta} disabled={!metaDirty || savingMeta}>
                      {savingMeta ? <><Spinner size="sm" className="me-2" />Saving…</> : "Save details"}
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xl={8}>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-0">Units and curricula</h5>
                    <small className="text-muted">
                      {units.length} unit(s) · {stepTotal} step(s). Each step is an existing curriculum; learners work through them in this order.
                    </small>
                  </div>
                  {!readOnly && (
                    <div className="d-flex gap-2">
                      <Button color="light" onClick={addUnit}><i className="mdi mdi-plus me-1" />Add unit</Button>
                      <Button color="primary" onClick={saveStructure} disabled={!structureDirty || savingStructure}>
                        {savingStructure ? <><Spinner size="sm" className="me-2" />Saving…</> : "Save units"}
                      </Button>
                    </div>
                  )}
                </div>

                <LearningPathErrorAlert message={structureError} onDismiss={() => setStructureError("")} />

                {structureDirty && !readOnly && (
                  <div className="alert alert-warning py-2">You have unsaved changes to units or curricula.</div>
                )}

                {!readOnly && (
                  <div className="mb-3">
                    <Input
                      bsSize="sm"
                      placeholder="Filter the curriculum lists below (title, slug, level, language)…"
                      value={curriculumFilter}
                      onChange={(e) => setCurriculumFilter(e.target.value)}
                    />
                  </div>
                )}

                {!units.length && (
                  <div className="text-muted py-3">No units yet.{!readOnly && " Click “Add unit” to start."}</div>
                )}

                {units.map((unit, unitIndex) => (
                  <div key={unit.key} className="border rounded p-3 mb-3">
                    <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
                      <h6 className="flex-grow-1 mb-0 mt-1" style={{ minWidth: 120 }}>
                        <Badge color="primary" className="fs-6">Unit {unitIndex + 1}</Badge>
                        <small className="text-muted ms-2 fw-normal">Named automatically by position</small>
                      </h6>
                      {!readOnly && (
                        <div className="d-flex gap-1">
                          <Button size="sm" color="light" title="Move unit up" disabled={unitIndex === 0} onClick={() => updateUnits((prev) => move(prev, unitIndex, -1))}><i className="mdi mdi-arrow-up" /></Button>
                          <Button size="sm" color="light" title="Move unit down" disabled={unitIndex === units.length - 1} onClick={() => updateUnits((prev) => move(prev, unitIndex, 1))}><i className="mdi mdi-arrow-down" /></Button>
                          <Button size="sm" color="danger" outline title="Remove unit" onClick={() => updateUnits((prev) => prev.filter((_, i) => i !== unitIndex))}><i className="mdi mdi-delete-outline" /></Button>
                        </div>
                      )}
                    </div>

                    <ol className="list-group list-group-numbered mb-2">
                      {unit.steps.map((step, stepIndex) => {
                        const c = curriculumById.get(step.curriculumId);
                        return (
                          <li key={step.key} className="list-group-item d-flex flex-wrap align-items-center gap-2">
                            <div className="flex-grow-1" style={{ minWidth: 200 }}>
                              <div className="fw-semibold">
                                {c ? c.title : `Curriculum #${step.curriculumId}`} {stepBadge(step.curriculumId)}
                              </div>
                              <small className="text-muted">
                                {c ? [c.slug, c.level, c.language].filter(Boolean).join(" · ") : "This curriculum no longer exists"}
                              </small>
                            </div>
                            <Input
                              bsSize="sm"
                              style={{ maxWidth: 220 }}
                              placeholder="Label (optional)"
                              maxLength={191}
                              value={step.title}
                              disabled={readOnly}
                              onChange={(e) => updateStep(unitIndex, stepIndex, { title: e.target.value })}
                            />
                            {!readOnly && (
                              <div className="d-flex gap-1">
                                <Button size="sm" color="light" title="Move up" disabled={stepIndex === 0}
                                  onClick={() => updateUnit(unitIndex, { steps: move(unit.steps, stepIndex, -1) })}><i className="mdi mdi-arrow-up" /></Button>
                                <Button size="sm" color="light" title="Move down" disabled={stepIndex === unit.steps.length - 1}
                                  onClick={() => updateUnit(unitIndex, { steps: move(unit.steps, stepIndex, 1) })}><i className="mdi mdi-arrow-down" /></Button>
                                <Button size="sm" color="danger" outline title="Remove step"
                                  onClick={() => updateUnit(unitIndex, { steps: unit.steps.filter((_, j) => j !== stepIndex) })}><i className="mdi mdi-close" /></Button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                      {!unit.steps.length && <li className="list-group-item text-muted">No curricula in this unit yet.</li>}
                    </ol>

                    {!readOnly && (
                      <Input
                        type="select"
                        bsSize="sm"
                        value=""
                        onChange={(e) => addStep(unitIndex, e.target.value)}
                      >
                        <option value="">+ Add a curriculum to this unit…</option>
                        {curriculumOptions.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.title}{c.level ? ` · ${c.level}` : ""}{c.language ? ` · ${c.language}` : ""}{c.isVisible ? "" : " (hidden)"}
                          </option>
                        ))}
                      </Input>
                    )}
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default withAuth(LearningPathEditor);
